import React from 'react';
import {
  Box,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography
} from '@mui/material';
import { rangeFilterSx } from 'common/table/styles/rangeFilterSx';
import { MinMax, TableV3FilterItem } from 'common/table/types/TableV3';
import {
  RangeFilterFields,
  TableV3RangeFilterPopover
} from 'common/table/components/TableV3RangeFilterPopover';
import { tableFilterItemsToMinMax } from 'common/table/utils/minMaxTableFilterItems';
import { isSupportedCalendarDate } from 'common/table/utils/rangeFilterLimits';

type TableV3DateRangeFilterButtonProps = {
  selectedItems: TableV3FilterItem[];
  isSorted?: boolean;
  onFilterApply: (items: TableV3FilterItem[]) => void;
};

type Comparator = 'before' | 'after' | 'between';

type MinOrMax = 'min' | 'max';

type DateRow = { month: string; day: string; year: string };

type Draft = { comparator: Comparator; min: DateRow; max: DateRow };

const COMPARATORS: { value: Comparator; label: string }[] = [
  { value: 'before', label: 'Before' },
  { value: 'after', label: 'After' },
  { value: 'between', label: 'Between' }
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const CALENDAR_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

const EMPTY_ROW: DateRow = { month: '', day: '', year: '' };

const calendarDateToDateRow = (value?: string): DateRow => {
  const calendarDate = value?.match(CALENDAR_DATE_PREFIX)?.[0];
  if (!calendarDate) return EMPTY_ROW;

  const [year, month, day] = calendarDate.split('-');
  return { month: String(Number(month)), day: String(Number(day)), year };
};

const minMaxToDraft = ({ min, max }: MinMax): Draft => ({
  comparator:
    min !== undefined && max !== undefined
      ? 'between'
      : min !== undefined
      ? 'after'
      : 'before',
  min: calendarDateToDateRow(min),
  max: calendarDateToDateRow(max)
});

const tableFilterItemsToDraft = (items: TableV3FilterItem[]) =>
  minMaxToDraft(tableFilterItemsToMinMax(items));

const dateRowToCalendarDate = ({
  month,
  day,
  year
}: DateRow): string | null => {
  if (!/^\d{1,2}$/.test(day) || !/^\d{4}$/.test(year) || !month) return null;
  const calendarDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
    2,
    '0'
  )}`;
  return isSupportedCalendarDate(calendarDate) ? calendarDate : null;
};

const draftToMinMax = ({ comparator, min, max }: Draft): MinMax | null => {
  const showsMin = comparator !== 'before';
  const showsMax = comparator !== 'after';
  const minDate = showsMin ? dateRowToCalendarDate(min) : null;
  const maxDate = showsMax ? dateRowToCalendarDate(max) : null;
  if ((showsMin && !minDate) || (showsMax && !maxDate)) return null;
  if (minDate && maxDate && minDate > maxDate)
    return { min: maxDate, max: minDate };

  return {
    ...(minDate ? { min: minDate } : {}),
    ...(maxDate ? { max: maxDate } : {})
  };
};

export const TableV3DateRangeFilterButton = (
  props: TableV3DateRangeFilterButtonProps
): React.ReactElement => {
  const renderFields = ({
    draft,
    setDraft,
    onKeyDown
  }: RangeFilterFields<Draft>) => {
    const setPart = (minOrMax: MinOrMax, part: keyof DateRow, value: string) =>
      setDraft((current) => ({
        ...current,
        [minOrMax]: { ...current[minOrMax], [part]: value }
      }));

    const renderRow = (minOrMax: MinOrMax, label?: string) => {
      const name = (part: string) =>
        label ? `${label} ${part.toLowerCase()}` : part;
      const row = draft[minOrMax];

      const renderField = (
        fieldLabel: string,
        sx: SxProps<Theme>,
        control: React.ReactNode
      ) => (
        <Box sx={sx}>
          <Typography sx={rangeFilterSx.title}>{fieldLabel}</Typography>
          {control}
        </Box>
      );

      return (
        <Box sx={rangeFilterSx.dateRow}>
          {renderField(
            name('Month'),
            rangeFilterSx.monthField,
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={row.month}
              onChange={(event) =>
                setPart(minOrMax, 'month', event.target.value)
              }
              renderValue={(value) =>
                value ? MONTHS[Number(value) - 1] : 'Month'
              }
              SelectDisplayProps={{ 'aria-label': name('Month') }}
              sx={rangeFilterSx.select}
            >
              {MONTHS.map((monthName, index) => (
                <MenuItem key={monthName} value={String(index + 1)}>
                  {monthName}
                </MenuItem>
              ))}
            </Select>
          )}
          {renderField(
            'Day (dd)',
            rangeFilterSx.dayField,
            <TextField
              size="small"
              placeholder="dd"
              value={row.day}
              onChange={(event) => setPart(minOrMax, 'day', event.target.value)}
              onKeyDown={onKeyDown}
              autoFocus={minOrMax === 'min' || draft.comparator === 'before'}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 2,
                'aria-label': name('Day')
              }}
              sx={rangeFilterSx.dateInput}
            />
          )}
          {renderField(
            'Year (yyyy)',
            rangeFilterSx.yearField,
            <TextField
              size="small"
              placeholder="yyyy"
              value={row.year}
              onChange={(event) =>
                setPart(minOrMax, 'year', event.target.value)
              }
              onKeyDown={onKeyDown}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 4,
                'aria-label': name('Year')
              }}
              sx={rangeFilterSx.dateInput}
            />
          )}
        </Box>
      );
    };

    return draft.comparator === 'between' ? (
      <>
        {renderRow('min', 'From')}
        {renderRow('max', 'To')}
      </>
    ) : (
      renderRow(draft.comparator === 'after' ? 'min' : 'max')
    );
  };

  return (
    <TableV3RangeFilterPopover
      {...props}
      comparators={COMPARATORS}
      tableFilterItemsToDraft={tableFilterItemsToDraft}
      draftToMinMax={draftToMinMax}
      renderFields={renderFields}
    />
  );
};
